import { isSalesViewEnabled } from '@ab-core-functions/sales-view';
import { userHasOnlySalesView } from '@ab-core-functions/user-permission';
import type { AvailabilitiesProps } from '@ab-core/availability';
import { useHandleSalesViewChange } from '@ab-core/functions';
import { useAuth } from '@ab-core/hooks';
import { usePositionPriceObject } from '@ab-core/hooks-v2';
import type { PricesType } from '@ab-core/prices';
import { useEffect, useState, type ReactNode } from 'react';
import type { DefaultScalableProductCardProps } from '.';
import type {
    AdditionalLinksContainerProps,
    AdditionalLinksContainerStyleType,
    IconsContainerProps,
    IconsContainerStyleType,
    IdsContainerProps,
    IdsContainerStyleType,
    ImageContainerProps,
    ImageContainerStyleType,
    InformationContainerType,
    NameContainerProps,
    NameContainerPropsStyleType,
    OrderContainerProps,
    OrderContainerStyleType,
    PositionTextContainerProps,
    PositionTextContainerStyleType,
    TagsContainerProps,
    TagsContainerStyleType
} from '../components';

const DEBOUNCE = 500;

import type { ComparableProductStyleType, ComparableProductType } from '@ab-core/product-comparison';
import type { InformationContainerPropsStyleType } from '../components/informationContainer';

type GetScalablePropsFromDefaultReturnType = {
    topContent?: ReactNode;
    tags?: TagsContainerProps & TagsContainerStyleType;
    image?: ImageContainerProps & ImageContainerStyleType;
    ids?: IdsContainerProps & IdsContainerStyleType;
    icons?: IconsContainerProps & IconsContainerStyleType;
    name?: NameContainerProps & NameContainerPropsStyleType;
    availability: AvailabilitiesProps;
    prices: PricesType;
    order: OrderContainerProps & OrderContainerStyleType;
    positionText: PositionTextContainerProps & PositionTextContainerStyleType;
    additionalLinks?: AdditionalLinksContainerProps & AdditionalLinksContainerStyleType;
    information: InformationContainerType & InformationContainerPropsStyleType;
    bottomContent?: ReactNode;
    comparison?: ComparableProductType & ComparableProductStyleType;
};

export const getScalablePropsFromDefaultProps = (
    props: DefaultScalableProductCardProps
): GetScalablePropsFromDefaultReturnType => {
    const {
        product,
        scalableStyle,
        topContent,
        bottomContent,
        isVisible,
        mainId,
        onQuantityChange: additionalOnQuantityChange,
        onPositionTextChange
    } = props;
    const {
        sku,
        defaultQuantity,
        defaultPositionText = '',
        tags,
        image,
        icons,
        ids,
        name,
        additionalLinks,
        prices,
        availability,
        comparison,
        order,
        information,
        externalProductPrice
    } = product;
    const [quantity, setQuantity] = useState(defaultQuantity);
    const [debounceQuantity, setDebounceQuantity] = useState(defaultQuantity);
    const [positionText, setPositionText] = useState(defaultPositionText);
    const [debouncePositionText, setDebouncePositionText] = useState(defaultPositionText);
    const { user } = useAuth();

    const skip = !(isVisible && user) || Boolean(externalProductPrice) || Boolean(order.hidePrice);

    const { positionPriceObject: internalPriceObject, refetch } = usePositionPriceObject({
        productId: sku,
        projectId: order.projectNumber ? parseInt(order.projectNumber, 10) : 0,
        chargeNr: Number(order?.chargeNr),
        quantity,
        skip
    });

    const positionPriceObject = externalProductPrice ? externalProductPrice : internalPriceObject;
    const onQuantityChange: OrderContainerProps['onQuantityChange'] = (quant) => {
        setDebounceQuantity(quant);
    };

    useEffect(() => {
        const handler = setTimeout(() => {
            setQuantity(debounceQuantity);
        }, DEBOUNCE);

        return () => {
            clearTimeout(handler);
        };
    }, [debounceQuantity]);

    useEffect(() => {
        setDebounceQuantity(defaultQuantity);
    }, [defaultQuantity]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncePositionText(debouncePositionText);
        }, DEBOUNCE);

        return () => {
            clearTimeout(handler);
        };
    }, [debouncePositionText]);

    const onPositionTextUpdate: PositionTextContainerProps['onPositionTextUpdate'] = (posText) => {
        setPositionText(posText);
    };

    const onlySalesView = userHasOnlySalesView();
    let hideBestPriceOnSalesView = false;

    const handleStorageChange = () => {
        hideBestPriceOnSalesView = isSalesViewEnabled(user?.activeAccount?.accountNumber);

        if (externalProductPrice) {
            return;
        }

        refetch({ hidePrice: hideBestPriceOnSalesView || onlySalesView, productId: product?.sku || '' });
    };

    useHandleSalesViewChange(handleStorageChange);

    useEffect(() => {
        if (!additionalOnQuantityChange) {
            return;
        }

        additionalOnQuantityChange(quantity);
    }, [quantity]);

    useEffect(() => {
        if (!onPositionTextChange) {
            return;
        }

        onPositionTextChange(positionText);
    }, [positionText]);

    return {
        topContent,
        bottomContent,
        information: { ...information, scalableStyle: scalableStyle?.information },
        image: { ...image, mainId, scalableStyle: scalableStyle?.image },
        name: { ...name, mainId, scalableStyle: scalableStyle?.name },
        comparison: { ...comparison, scalableStyle: scalableStyle?.comparison },
        additionalLinks: { ...additionalLinks, scalableStyle: scalableStyle?.additionalLinks },
        availability: { ...availability, quantity, availabilitiesStyles: scalableStyle?.availabilities },
        prices: {
            ...prices,
            quantity,
            positionPrice: positionPriceObject,
            pricesStyles: scalableStyle?.prices,
            cartId: order.cartId,
            sku: order.sku,
            hidePrice: order.hidePrice
        },
        order: {
            ...order,
            onQuantityChange,
            positionText,
            quantity,
            showPrices: scalableStyle?.order?.showPrices,
            mainId,
            hideBestPriceOnSalesView,
            positionPrice: positionPriceObject,
            scalableStyle: scalableStyle?.order,
            hidePrice: order.hidePrice,
            isPackageItem: order.isPackageItem
        },
        icons: { ...icons, scalableStyle: scalableStyle?.icons },
        ids: { ...ids, scalableStyle: scalableStyle?.ids },
        tags: { ...tags, scalableStyle: scalableStyle?.tags },
        positionText: { onPositionTextUpdate, positionText, scalableStyle: scalableStyle?.positionText }
    };
};
